import { Tooltip } from '@/components/ui/tooltip';
import type { ReactElement } from 'react';

/**
 * The bubble that carries a persona's PROSE: the direction of an involvement
 * cran, and the text of a tone (issue #104; refonte modulaire §10.1).
 *
 * ## One component, mounted twice, and that is the decision
 *
 * A tone tag and a point of the slider carry the same THING - a piece of
 * writing one reads on hover to decide what sits under the pointer, on the same
 * screen, in the same column of the same form. Two widths and two text sizes
 * for that would be a distinction nobody could explain, so the two the mockups
 * draw (320 px over the cran, 300 px over the tag) are settled HERE at one
 * value rather than at each call site.
 *
 * ## The mockups are stale on the COLOUR, and only on the colour
 *
 * `docs/specs/maquettes/personas/*.dc.html` draw both of these bubbles dark -
 * a near-black ground, white ink. The design system decides against them: white
 * is the one ground of the PMS brand, even for a transitory component, and the
 * charte guard of `design-system-adherence.test.ts` refuses a dark surface
 * outright. Nothing here paints a ground at all: the shared `Tooltip` of issue
 * #103 already IS the white bubble. The mockups are read for their sizes.
 *
 * ## The width and the silence are ASKED FOR, by name
 *
 * `Tooltip` is sized for a LABEL - 240 px, two lines - which this is not, and a
 * width class on this paragraph could never widen its parent: a child does not
 * defeat an ancestor's `max-width`, it overflows it. A first pass reached for a
 * rule whose subject was the bubble (`[role=tooltip]:has(> &)` plus an `!`, the
 * only thing that beats an inline style) and it worked, but it was a call site
 * reaching into a component through CSS. The component takes a `maxWidth` now,
 * and the twenty-eight generic sites keep the default by saying nothing.
 *
 * `describes={false}` is the same move for the same reason - see below.
 *
 * ## It WRAPS, and nothing about it truncates
 *
 * No `truncate`, no ellipsis, no fade - « wraps before truncates ». The schema
 * caps both texts at 2000 characters (`PERSONA_TONE_TEXT_MAX`,
 * `INVOLVEMENT_LEVEL_DIRECTION_MAX`), which is some nine hundred pixels of
 * wrapped prose at this width - taller than a laptop window.
 *
 * A first draft claimed `lib/floating.ts` bounded that, and it does not: the
 * placer clamps the panel's top CORNER into the window, never its height, so
 * the tail simply hung below the fold with no way to reach it. The bubble
 * itself now stops at the height of the window and SCROLLS, which it can only
 * afford because it is hoverable - the pointer that goes for the wheel does not
 * lose it. Nothing is cut, and nothing is hidden; it is just read in two goes.
 *
 * ## It DESCRIBES nothing, and the `sr-only` sibling is why
 *
 * Both call sites already point at an `sr-only` copy of this very sentence with
 * `aria-describedby`, and it STAYS: it is what answers the keyboard and the
 * touch screen, where the text has to exist without a hover. `Tooltip` would
 * otherwise append its own id to that attribute while it is open, and the same
 * sentence would be read out TWICE in a row. It is the defect the component
 * already guards against for the accessible NAME (« Expériences, Expériences »);
 * its guard compares against `aria-label` and cannot see a description that
 * already exists, so the call site says so. The bubble is then what it is here:
 * a drawing, for the eye that hovers.
 */

/**
 * The line breaks the operator typed, and nothing else.
 *
 * `whitespace-pre-line` is not decoration: a tone's text is entered in a
 * TEXTAREA, the native `title` it replaces rendered its newlines, and dropping
 * them would silently reflow somebody's paragraphs into one block. It still
 * wraps, so a long line is still folded rather than cut.
 */
const PROSE = 'whitespace-pre-line';

/**
 * One width for both, settled here.
 *
 * The mockups draw 320 px over a cran and 300 over a tag. The decision of the
 * grill is that these are the same bubble, so the two become one, and 320 is
 * the figure drawn around the longer of the two texts.
 */
const PROSE_WIDTH = 320;

export interface PersonaProseTooltipProps {
  /**
   * The sentence itself. Never empty: both schemas bound it at `min(1)`, which
   * is why there is no « no bubble » branch here.
   */
  readonly text: string;
  /**
   * The control it sits over - ONE element that accepts a `ref` and pointer
   * handlers, which is the whole contract `Tooltip` imposes (it clones rather
   * than wraps, so nothing of the layout moves).
   */
  readonly children: ReactElement;
}

export function PersonaProseTooltip({ text, children }: PersonaProseTooltipProps) {
  return (
    <Tooltip maxWidth={PROSE_WIDTH} describes={false} label={<p className={PROSE}>{text}</p>}>
      {children}
    </Tooltip>
  );
}
